import { useState, useRef, useContext, useEffect } from "react";
import "./Header.scss";
import logo from "../../assests/logo.png";
import { Dialog } from "../Modal";
import SideBarComponent from "../common/SideBar";
import { getResponse, getToken, removeTokenAndResponse } from "../../apis/util";
import Profile from "../modal/Profile";
import History from "../modal/History";
import { FaTachometerAlt, FaUserAlt } from "react-icons/fa";
import { TbLogout } from "react-icons/tb";
import { RxCounterClockwiseClock } from "react-icons/rx";
import { HiMenu, HiChevronDown, HiChevronUp } from "react-icons/hi";
import { Popover, Overlay } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { userLogout } from "../../apis/authApis";
import { ProfileContext } from "../../contexts/APIProvider";
import { preloadImages } from "../../preload/imageCache";
import { HOME_IMAGES, GAME_IMAGES } from "../../preload/pageAssets";
import { ACCELERATOR_IMAGES } from "../pages/Accelerators/Constant";

// Existing Blogs target (preserved from the old games sidebar config).
const BLOGS_URL =
  "https://www.nagarro.com/en/blog";

interface NavItem {
  label: string;
  action: () => void;
  prefetch?: () => void;
  isActive?: boolean;
}

export function Header() {
  const [show, setShow] = useState<boolean>(false);
  const [isPopOver, setIsPopOver] = useState<boolean>(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const ref = useRef(null);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const xrftoken = getToken();
  const [token, setToken] = useState<boolean>(!!xrftoken);
  const response = getResponse() as { username?: string; wallet?: number };
  const [showProfile, setShowProfile] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const { setLoggedIn } = useContext(ProfileContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/home";
  const isGame = location.pathname === "/game";
  const isPortfolio = location.pathname.startsWith("/portfolio");
  //const isAccelerators = location.pathname === "/accelerators";

  const gotoGames = () => {
    navigate("/game");
  };

  const closeSidebar = () => {
    setShowSidebar(false);
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handlePopOver = (event: React.MouseEvent<HTMLElement>) => {
    setIsPopOver((prev) => !prev);
    setTarget(event.currentTarget);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openBlogs = () => {
    window.open(BLOGS_URL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    setToken(!!xrftoken);
  }, [xrftoken]);

  useEffect(() => {
    if (token) {
      setLoggedIn(true);
    }
  }, [token, setLoggedIn]);

  useEffect(() => {
    setShowProfile(false);
    setShowHistory(false);
    setIsPopOver(false);
    setShowSidebar(false);
    setShow(false);
  }, [location.pathname]);

  useEffect(() => {
    
    const getScrollTop = () =>
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const onScroll = () => setScrolled(getScrollTop() > 40);
    onScroll();
    
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [location.pathname]);

  const handleLogout = async () => {
      setIsPopOver(false);
    setTarget(null);
    try {
      await userLogout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setLoggedIn(false);
      removeTokenAndResponse();
      setToken(false);
    }
  };

  const navItems: NavItem[] = [
    {
      label: "Home",
      action: isHome ? scrollToTop : () => navigate("/home"),
      prefetch: isHome ? undefined : () => preloadImages(HOME_IMAGES),
      isActive: isHome,
    },
    {
      label: "Explore Games",
      action: gotoGames,
      prefetch: isGame ? undefined : () => preloadImages(GAME_IMAGES),
      isActive: isGame,
    },
    // {
    //   label: "Accelerators",
    //   action: () => navigate("/accelerators"),
    //   prefetch: isAccelerators ? undefined : () => preloadImages(ACCELERATOR_IMAGES),
    //   isActive: isAccelerators,
    // },
    {
      label: "Art Portfolio",
      action: () => navigate("/portfolio/concept-art"),
      isActive: isPortfolio,
    },
    { label: "Blogs", action: openBlogs },
  ];

  const handleNav = (action: () => void) => {
    action();
    closeSidebar();
  };

  return (
    <header className={`site-header ${scrolled ? "site-header--scrolled" : ""}`}>
      <div className="site-header__inner">
        <a
          href="/home"
          className="site-header__logo"
          aria-label="Go to Home"
          onClick={(e) => {
            e.preventDefault();
            if (isHome) {
              scrollToTop();
            } else {
              navigate("/home");
            }
          }}
        >
          <img src={logo} alt="Nagarro" height={36} />
      </a>

        <nav className="site-header__nav" aria-label="Primary" ref={ref}>
          {navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`nav-link ${item.isActive ? "is-active" : ""}`}
              aria-current={item.isActive ? "page" : undefined}
              onMouseEnter={item.prefetch}
              onFocus={item.prefetch}
              onClick={() => item.action()}
            >
              {item.label}
            </button>
          ))}

          {token ? (
            <button
              type="button"
              className="nav-link nav-link--signin nav-link--user"
              onClick={handlePopOver}
        >
              {response?.username}
              {isPopOver ? (
                <HiChevronUp className="nav-link__caret" />
              ) : (
                <HiChevronDown className="nav-link__caret" />
          )}
            </button>
          ) : isGame ? (
            <button
              type="button"
              className="nav-link nav-link--signin"
              onClick={handleShow}
            >
              Sign In
            </button>
          ) : null}
        </nav>

        <button
          type="button"
          className="hamburger"
          onClick={() => setShowSidebar(true)}
          aria-label="Open menu"
        >
          <HiMenu />
        </button>
      </div>

        <Overlay
          show={isPopOver}
          target={target}
          rootClose
          onHide={() => setIsPopOver(false)}
          placement="bottom-end"
          container={ref}
          containerPadding={10}
        >
        <Popover id="popover-contained" className="user-popover">
            <Popover.Body>
              {token && (
                <div className="dropdown-content">
                  <button
                    onClick={() => {
                      setShowProfile(true);
                      setIsPopOver(false);
                    }}
                    className="headerButton"
                  >
                    Profile <FaUserAlt className="UserLoginIcon" />
                  </button>
                  <hr />
                  <button
                    onClick={() => {
                      setShowHistory(true);
                      setIsPopOver(false);
                    }}
                    className="headerButton"
                  >
                  History <RxCounterClockwiseClock className="UserLoginIcon" />
                  </button>
                  <hr />
                  <button className="headerButton">
                    <div>
                      Balance:{" "}
                      <span id="balance">
                        {response?.wallet ? response.wallet.toFixed(2) : ""}
                      </span>
                    </div>
                    <FaTachometerAlt className="UserLoginIcon" />
                  </button>
                  <hr />
                  <button className="headerButton" onClick={handleLogout}>
                    Logout <TbLogout className="UserLoginIcon" />
                  </button>
                </div>
              )}
            </Popover.Body>
          </Popover>
        </Overlay>

        <Profile open={showProfile} handleClose={() => setShowProfile(false)} />
        {token && (
        <History open={showHistory} handleClose={() => setShowHistory(false)} />
        )}

      <SideBarComponent
        OffcanvasClass="mobile-nav"
        show={showSidebar}
        placement="end"
        handleClose={closeSidebar}
      >
        <div className="mobile-nav__content">
          <nav className="mobile-nav__links" aria-label="Mobile primary">
            {navItems.map((item) => (
        <button
                key={item.label}
          type="button"
                className={`mobile-nav__link ${item.isActive ? "is-active" : ""}`}
                aria-current={item.isActive ? "page" : undefined}
                onClick={() => handleNav(item.action)}
        >
                {item.label}
        </button>
            ))}
          </nav>

          <div className="mobile-nav__auth">
            {token ? (
              <>
                <button
                  type="button"
                  className="mobile-nav__link"
                  onClick={() => {
                    setShowProfile(true);
                    closeSidebar();
                  }}
        >
                  Profile
                </button>
                <button
                  type="button"
                  className="mobile-nav__link"
                  onClick={() => {
                    setShowHistory(true);
                    closeSidebar();
                  }}
                >
                  History
                </button>
                <button
                  type="button"
                  className="signin-btn signin-btn--block"
                  onClick={() => {
                    handleLogout();
                    closeSidebar();
                  }}
                >
                  Logout
                </button>
              </>
            ) : isGame ? (
              <button
                type="button"
                className="signin-btn signin-btn--block"
                onClick={() => {
                  handleShow();
                  closeSidebar();
                }}
              >
                Sign In
              </button>
            ) : null}
      </div>
    </div>
      </SideBarComponent>

      <Dialog show={show} handleClose={handleClose} />
    </header>
  );
}
